import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/Support.css';

const supportEmail = 'support@equationlive.com';

function Support() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    image: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const faqItems = [
    {
      question: "How do I install the extension?",
      answer: (
        <div>
          <p>Follow the link and click 'Add to Chrome'. After installation, you'll need to create an account and subscribe to start using the extension.</p>
          <button 
            className="support-button"
            onClick={() => window.open(process.env.REACT_APP_CHROME_WEBSTORE_EXTENSION_LINK)}
          >
            Chrome Web Store
          </button>
        </div>
      )
    },
    {
      question: "What if I forgot my password?",
      answer: (
        <div>
          <p>Use the 'Forgot Password' link on the sign in page to reset your password.</p>
          <button 
            className="support-button"
            onClick={() => navigate('/forgot-password')}
          >
            Reset Password
          </button>
        </div>
      )
    },
    {
      question: "How do I delete my account?",
      answer: "To delete your account, please contact us at support@equationlive.com. We will assist you with the process."
    },
    {
      question: "What if my payment fails?",
      answer: "If payment fails, you will lose access to the extension. Our servers will automatically retry the payment every 24 hours. If it fails again or you have any other issues, please contact us at support@equationlive.com. We will assist you with the process."
    },
    {
      question: "How do I report a bug?",
      answer: "You can report bugs by filling out the form on this page or by sending an email to support@equationlive.com. Please include details about the issue, your browser version, and any steps to reproduce the problem."
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null
    }));
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const emailSubject = encodeURIComponent(formData.subject);
      const emailBody = encodeURIComponent(`
        Name: ${formData.name}
        
        ${formData.message}

        --------------------------------
        Sent from Equation Live Support Form
      `);

      const mailtoLink = `mailto:${supportEmail}?subject=${emailSubject}&body=${emailBody}`;
     
      window.open(mailtoLink, '_blank');
      
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        image: null
      });
      setPreviewUrl(null);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="support-container">
      <nav className="support-nav">
        <div className="nav-content">
          <div className="logo" onClick={() => navigate('/')}>Equation Live</div>
          <div className="nav-buttons">
            <button className="nav-button primary" onClick={() => navigate('/signin')}>
              Sign In
            </button>
          </div>
        </div>
      </nav>

      <div className="support-content">
        <div className="support-header">
          <h1>Support Center</h1>
          <p>We're here to help you with any questions or issues you might have.</p>
        </div>

        <div className="support-grid">
          <div className="support-form-container">
            <h2>Contact Us</h2>
            <form className="support-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Your email address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="What's this about?"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="How can we help you?"
                  rows="6"
                />
              </div>

              <div className="form-group">
                <label htmlFor="image">Attach Screenshot (Optional)</label>
                <div className="image-upload-container">
                  <input
                    type="file"
                    id="image"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    className="image-input"
                  />
                  <div className="image-upload-box">
                    {previewUrl ? (
                      <div className="image-preview">
                        <img src={previewUrl} alt="Preview" />
                        <button
                          type="button"
                          className="remove-image"
                          onClick={handleRemoveImage}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <span className="upload-icon">📷</span>
                        <span className="upload-text">Click to upload image</span>
                        <span className="upload-hint">Max size: 5MB</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="success-message">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="error-message">
                  Sorry, there was an error sending your message. Please try again.
                </div>
              )}

              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          <div className="support-info">

            <div className="info-card">
              <h3>Response Time</h3>
              <p>We typically respond to all inquiries within 24 hours during business days.</p>
            </div>

            <div className="info-card">
              <h3>Common Questions</h3>
              <ul className="faq-list">
                {faqItems.map((item, index) => (
                  <li 
                    key={index} 
                    className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                    onClick={() => toggleFaq(index)}
                  >
                    <div className="faq-question">
                      {item.question}
                      <span className="faq-icon">{expandedFaq === index ? '−' : '+'}</span>
                    </div>
                    {expandedFaq === index && (
                      <div className="faq-answer">
                        {item.answer}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Equation Live</h4>
            <p>Making LaTeX editing and rendering easy in ChatGPT</p>
          </div>
          <div className="footer-section">
            <ul>
              <li><a href="/support">Support</a></li>
              <li><a href="/about">About</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Equation Live. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Support; 